import {
	type BreakdownContainer,
	type BreakdownDatatable,
	type DatatableStructureComponent,
	type DatatableStructureContainer,
	type DatatableStructureResponse,
	FundBreakdownsResponseSchema,
} from "@/types";
import type { SrpRequestParams } from "hsl-core/SRP";
import { SRP } from "hsl-core/providers";

export type BreakdownSectionConfig = {
	container?: BreakdownContainer;
	components: BreakdownDatatable[];
};

export type BreakdownSection = {
	container?: DatatableStructureContainer;
	components: DatatableStructureComponent[];
	data?: BreakdownSectionConfig;
};

type BreakdownSectionMap = {
	asAtDate: Date;
	fixed: BreakdownSection;
	tabs: BreakdownSection[];
	others: BreakdownSection[];
};

export default function useBreakdownSections(
	structureURL?: string,
	data?: DatatableStructureResponse,
	params?: SrpRequestParams,
	baseURL?: string,
): BreakdownSectionMap | null {
	const url = new URL(window.location.href);
	const isStaging = ["huguenots.co.uk", "int.rlam.com"].some((host) =>
		url.host.includes(host),
	);
	const as_at_date = url.searchParams.get("as_at_date");
	const [id] = `${structureURL}`.match(/(\d+)/) ?? [];
	const pathname = `/srp/api/advanced-datatable-configuration/${id}/`;
	const enabled = !!id;
	const tempParams = as_at_date && isStaging ? { as_at_date } : {};
	const {
		data: configuration,
		isLoading,
		error,
	} = SRP.useSRP(
		pathname,
		enabled,
		{ ...params, ...tempParams },
		FundBreakdownsResponseSchema,
		baseURL,
	);
	if (!data || !structureURL || structureURL.length === 0) {
		return null;
	}

	const isFixed = (key: string) => key === "fixed";
	const isTab = (key: string) => key === "tabbed";
	const isOther = (key: string) => !isFixed(key) && !isTab(key);
	const byRank = (a: { rank: number }, b: { rank: number }) => a.rank - b.rank;

	if (isLoading || error || !configuration) {
		return null;
	}

	// configuration container_name is the zone name, this will change
	const fixedConfigContainer = configuration.containers.find((c) =>
		isFixed(c.container_name),
	);
	const tabConfigContainers = configuration.containers.filter((c) =>
		isTab(c.container_name),
	);
	const otherConfigContainers = configuration.containers.filter((c) =>
		isOther(c.container_name),
	);

	const fixedContainer = data.containers.find(
		(c) => c.key === fixedConfigContainer?.key,
	);
	const tabsContainers = data.containers.filter((c) =>
		tabConfigContainers.find((t) => t.key === c.key),
	);
	const otherContainers = data.containers.filter((c) =>
		otherConfigContainers.find((t) => t.key === c.key),
	);

	return {
		asAtDate: configuration.as_at_date,
		fixed: {
			container: fixedContainer,
			components: data.components.filter(
				(c) => c.container === fixedContainer?.key,
			),
			data: {
				container: fixedConfigContainer,
				components: configuration.datatables.filter(
					(t) => t.container === fixedConfigContainer?.key,
				),
			},
		},
		tabs: tabsContainers.sort(byRank).map((tc) => ({
			container: tc,
			components: data.components
				.filter((c) => c.container === tc.key)
				.sort(byRank),
			data: {
				container: configuration.containers.find((c) => c.key === tc.key),
				components: configuration.datatables.filter(
					(t) => t.container === tc.key,
				),
			},
		})),
		others: otherContainers.sort(byRank).map((oc) => ({
			container: oc,
			components: data.components
				.filter((c) => c.container === oc.key)
				.sort(byRank),
			data: {
				container: configuration.containers.find((c) => c.key === oc.key),
				components: configuration.datatables.filter(
					(t) => t.container === oc.key,
				),
			},
		})),
	};
}
